/**
 * §2.8 — one language mechanism, shared by every console.
 *
 * Two things live here, and they are separate on purpose:
 *
 *   - **words**, which a translator supplies (`catalogue`, `translator`), and
 *   - **shapes**, which a locale supplies (`groupDigits`, `decimalSeparator`,
 *     `dateFieldOrder`) — the separators and field order a number and a date
 *     take in the reader's language.
 *
 * A console can have one without the other. Shapes work the moment a locale is
 * set, with nothing translated; words work with the shapes left in English.
 * Splitting them is what let this land as one change rather than as a rewrite
 * of every screen: `formatMoney` became locale-aware without a single call site
 * moving, because the locale is asked for rather than passed down.
 *
 * §2.8b is the line neither side crosses. Rule IDs, reason codes, event types,
 * statuses and currency codes are identifiers. Nothing here translates one, and
 * `identifierLike()` exists so a test can prove no catalogue ever starts.
 *
 * No framework and no DOM: this is about wording and arithmetic, and it is
 * tested without mounting anything.
 */

import { LOCALE_TAG as TAG, SHIPPED_LOCALES, SHIPPED_LOCALE_TAGS } from '@affdice/rules/locales';
import { translator } from '@affdice/rules/catalogue';
import type { Catalogue, Locale, Params, Translator } from '@affdice/rules/catalogue';

/**
 * §2.8a / §2.8f (E37) — the languages on offer, in both consoles.
 *
 * One list, owned by `@affdice/rules`, because `api-core` refuses a tag that
 * is not on it and the consoles offer exactly what is: a picker and a
 * validator reading two lists is how a saved language stops being an offered
 * one. Re-exported under the name the consoles have always imported.
 */
export const LOCALES = SHIPPED_LOCALES;

/*
 * The catalogue, the fallback chain and the lookup itself moved to
 * `@affdice/rules` when the mailer needed them. A console asks for the
 * language *this tab* reads in and a mailer asks for the language *this
 * recipient* reads in — the same lookup, a different reader — and duplicating
 * it would have given the two of them different fallback rules the first time
 * either changed.
 *
 * Re-exported under the names the consoles have always imported, so nothing
 * downstream had to move with it.
 */
export {
  identifierLike,
  localeChain,
  translator,
  untranslated,
  type Catalogue,
  type Locale,
  type Message,
  type Params,
  type Translator,
} from '@affdice/rules/catalogue';

/* ------------------------------------------------------------------ *
 * The display locale — §2.8c's shapes
 * ------------------------------------------------------------------ */

/**
 * The language this browser tab is currently reading in.
 *
 * Module state, and that is not a shortcut. `portal-kit` is consumed by three
 * browser consoles and by nothing on the server — no API route, no worker, no
 * mailer imports it — so the process it runs in has exactly one reader with
 * exactly one language. Threading a locale through ninety `formatMoney` call
 * sites to express "the one person looking at this screen" would put the same
 * value in every argument and add a way to get it wrong in one of them.
 *
 * The moment something renders for *another* person — a statement PDF built
 * server-side, a mail body composed for a recipient — this stops being true,
 * and the parameter on `formatMoney` is there for exactly that: pass the
 * recipient's locale explicitly and no global is consulted.
 */
let current: Locale = 'en';
let fallback: Locale = 'en';

/**
 * §2.8d — the language this tab reads in, and the one it falls back to.
 *
 * Both, in one call, because they are one decision: `chosen` is what the
 * reader picked (or nothing, when they never have), and `operatorDefault` is
 * what applies to them until they do. The display locale is the first of the
 * two that is a real tag; the operator default is kept beside it so a
 * translator built for this tab looks there *before* English — the hop in the
 * fallback chain that a translator built from the display locale alone can
 * never take, because by then the default has been folded into it.
 *
 * Omitting `operatorDefault` resets it to English rather than leaving the
 * last one standing: the one place that matters is a test, and a test that
 * sets `pt-BR` as the default and then "resets" with `setDisplayLocale('en')`
 * should be back where it started.
 */
export function setDisplayLocale(
  chosen: Locale | null | undefined,
  operatorDefault?: Locale | null,
): void {
  fallback = operatorDefault && TAG.test(operatorDefault) ? operatorDefault : 'en';
  current = chosen && TAG.test(chosen) ? chosen : fallback;
}

/**
 * §2.8d before anybody has signed in — the one hop the chain cannot make.
 *
 * `setDisplayLocale` is called from `me()`, which needs a session. Everything
 * before one — the sign-in form, the §3.7 onboarding steps, an invitation, a
 * signup — therefore rendered in English however the reader's browser was set,
 * and for somebody who cannot read English that is the *first* screen and the
 * one they cannot get past. The saved choice still wins the moment there is a
 * session to read it from; this only decides what to show while there is not.
 *
 * Matched against the shipped list rather than used raw. An exact tag wins
 * (`pt-BR`), then a tag whose base language we ship (`es-MX` → `es`) — the
 * widening step `localeChain` already makes. Narrowing is not done here for the
 * same reason it is not done there: `pt-PT` does not become `pt-BR`, because
 * choosing a region nobody asked for is a guess about currency, date order and
 * idiom.
 */
export function preferredShippedLocale(
  languages: readonly string[] = [],
): Locale | null {
  const shipped = SHIPPED_LOCALE_TAGS as readonly string[];
  for (const tag of languages) {
    if (!tag || !TAG.test(tag)) continue;
    if (shipped.includes(tag)) return tag;
  }
  for (const tag of languages) {
    if (!tag || !TAG.test(tag)) continue;
    const base = tag.slice(0, 2);
    if (shipped.includes(base)) return base;
  }
  return null;
}

export function displayLocale(): Locale {
  return current;
}

/** The operator default this tab falls back to (§2.8d). `en` until told otherwise. */
export function operatorDefaultLocale(): Locale {
  return fallback;
}

/**
 * A translator for this tab: the display locale, then the operator default,
 * then English — §2.8d's whole chain, which is why the consoles' `t()` build
 * theirs through this rather than from `displayLocale()` alone. Cached per
 * (locale, default) pair, because `t()` runs on every render of every label.
 */
export function tabTranslator<C extends Catalogue>(
  catalogue: C,
  cache: Map<string, Translator<C>>,
): Translator<C> {
  const key = `${current}\u0000${fallback}`;
  let made = cache.get(key);
  if (!made) {
    made = translator(catalogue, current, fallback);
    cache.set(key, made);
  }
  return made;
}

/* ------------------------------------------------------------------ *
 * Locale-shaped numbers, without the amount ever becoming a number
 * ------------------------------------------------------------------ */

/*
 * §1.3a and §2.8c pull in opposite directions here, and both win.
 *
 * §1.3a: an amount is minor units in a string and never becomes a float,
 * because 9007199254740993 minor units is a real balance and a float is not
 * where you keep it. §2.8c: it is written for the reader's locale.
 *
 * `Intl.NumberFormat` accepts a `BigInt`, which is the whole reconciliation:
 * the integer part is grouped exactly, at any size, by the same CLDR data that
 * knows `en-IN` groups 12,34,567 and not 1,234,567. The fraction digits are
 * sliced off the original string and never go near it. The only float in this
 * file is the literal 1.5, used once to ask a locale what its decimal
 * separator looks like.
 */
const groupers = new Map<Locale, Intl.NumberFormat>();

/*
 * `'always'`, not `true`. Before ES2023 `true` meant "always", and since it
 * means "auto" — which Spanish resolves to *no* separator below five digits
 * (`1234`, not `1.234`), per CLDR's minimum grouping digits. `'always'` is the
 * grouping the tests assert; the cast is because the TS `lib` this package
 * compiles against still types the option as a boolean.
 */
const GROUPED: Intl.NumberFormatOptions = {
  useGrouping: 'always' as unknown as boolean,
  maximumFractionDigits: 0,
};
const decimals = new Map<Locale, string>();

/**
 * A tag can reach here from a database column, so a malformed one must not
 * take the screen down. `Intl` throws `RangeError` on a bad tag; English is
 * the answer, and it is the same answer §2.8d gives.
 */
function grouper(locale: Locale): Intl.NumberFormat {
  const cached = groupers.get(locale);
  if (cached) return cached;
  let made: Intl.NumberFormat;
  try {
    made = new Intl.NumberFormat(locale, GROUPED);
  } catch {
    made = new Intl.NumberFormat('en', GROUPED);
  }
  groupers.set(locale, made);
  return made;
}

/** Digit-group separator applied to an integer string of any length. */
export function groupDigits(digits: string, locale: Locale = current): string {
  // Not a plain integer — a corrupted amount, or a caller passing something
  // else. Grouping it would make nonsense look like a figure.
  if (!/^\d+$/.test(digits)) return digits;
  return grouper(locale).format(BigInt(digits));
}

/** `.` in English, `,` in Spanish and Brazilian Portuguese. */
export function decimalSeparator(locale: Locale = current): string {
  const cached = decimals.get(locale);
  if (cached !== undefined) return cached;
  let found = '.';
  try {
    for (const part of new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
    }).formatToParts(1.5)) {
      if (part.type === 'decimal') found = part.value;
    }
  } catch {
    found = '.';
  }
  decimals.set(locale, found);
  return found;
}

/** Digit-group separator this locale writes — `,` in English, `.` in Spanish. */
export function groupSeparator(locale: Locale = current): string {
  for (const part of grouper(locale).formatToParts(1234567)) {
    if (part.type === 'group') return part.value;
  }
  return '';
}

/**
 * A whole number the reader typed, or null when what they typed is not one.
 *
 * The other half of {@link groupDigits}: a screen that prints "1,240" must
 * read "1,240" back. `Number.parseInt` cannot — it stops at the first
 * character it does not like and returns what it had, so "1,240" is 1 and
 * "12abc" is 12. Silently. On the click-discrepancy screen that turned an
 * affiliate's "my tracker counted 1,240" into a comparison against 1, and the
 * gap it drew underneath was the one they would have quoted in a dispute.
 *
 * Locale-aware because the separator is: `1.240` is one thousand two hundred
 * and forty to a Spanish or Brazilian reader and a decimal to an English one,
 * and a parser that only knows the comma gets one of them wrong. A separator
 * in the wrong places (`1,2,3`) is refused rather than guessed at — the
 * grouping is not re-derived here, only accepted or rejected as digits.
 *
 * Null, never NaN and never a partial read: the caller has to decide what to
 * tell the reader, which is the whole point.
 */
export function parseWholeNumber(text: string, locale: Locale = current): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const sep = groupSeparator(locale);
  // A non-breaking or thin space is what `Intl` groups with in some locales,
  // and what a paste from a spreadsheet carries either way.
  const bare = (sep === '' ? trimmed : trimmed.split(sep).join('')).replace(/[  \s]/g, '');
  if (!/^\d+$/.test(bare)) return null;
  const n = Number(bare);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * The order a locale writes a date's fields in — `['month','day','year']` for
 * English, `['day','month','year']` for Spanish and Portuguese.
 *
 * Asked of `Intl` rather than tabulated, because a table of date orders is a
 * table that is wrong for some language nobody on this project speaks.
 */
export function dateFieldOrder(locale: Locale = current): readonly string[] {
  const order: string[] = [];
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    format = new Intl.DateTimeFormat('en', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  for (const part of format.formatToParts(new Date(Date.UTC(2026, 3, 3)))) {
    if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
      order.push(part.type);
    }
  }
  return order;
}
